import { zipObject } from 'lodash';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { sqMaterializedTablesApi } from '@/sdk/api/MaterializedTablesApi';
import { sqTablesApi } from '@/sdk/api/TablesApi';
import { downloadBlob } from '@/utilities/download.utilities';
import type { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import type { Filter as SdkFilter } from '@/sdk/model/Filter';
import type { TableResultOutputV1 } from '@/sdk/model/TableResultOutputV1';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { GroupBy, VirtualColumnType } from '@/serverSideTable/serverSideTable.constants';
import type { AnyProperty } from '@/utilities.types';

export interface Event {
  properties: { name: string; value: unknown; unitOfMeasure?: string }[];
}

export interface GetTableParams {
  limit: number;
  columns: string[];
  properties?: AnyProperty[];
  filter?: Filter;
  sorts?: { columnName: string; ascending: boolean }[];
  cursor?: { cursorKey: string };
  useDistinct?: boolean;
  virtualColumns?: Record<VirtualColumnType, string[]>;
  groupBy?: GroupBy;
  conditionMonitorId?: string;
  distinctOnColumns?: string[];
}

export async function publish(tableId: string, events: Event[]) {
  const response = await sqMaterializedTablesApi.publishEvents(
    {
      events: events.map((event) => ({ genericEvent: event })),
    },
    { tableId },
  );
  return response.data;
}

export function tabulate({ rows, headers }: TableResultOutputV1): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);

  return rows.map((row) => zipObject(headerNames, row));
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
    useDistinct,
    virtualColumns,
    groupBy,
    conditionMonitorId,
    distinctOnColumns = [],
  }: GetTableParams,
  cancellationGroup?: string,
) {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }

  const { data: tableResult } = await sqTablesApi.queryTableData(
    {
      limit,
      columnsToInclude: columns,
      propertiesToInclude: properties,
      filter: filter as SdkFilter | undefined,
      sorts,
      cursor,
      useDistinct,
      virtualColumns,
      groupBy,
      conditionMonitorId,
      distinctOnColumns,
    },
    { tableId },
    config,
  );

  return { data: tabulate(tableResult), cursorKey: tableResult.cursorKey };
}

interface FetchDistinctValuesForColumnParams extends Pick<
  GetTableParams,
  'filter' | 'groupBy' | 'distinctOnColumns' | 'limit'
> {
  tableDefinitionId: string;
  columnName: string;
  virtualColumnKey?: VirtualColumnType;
  extraColumnsToInclude?: string[];
}

export async function fetchDistinctValuesForColumn<ColumnType = string>({
  tableDefinitionId,
  columnName,
  limit,
  virtualColumnKey,
  filter,
  groupBy,
  extraColumnsToInclude = [],
  distinctOnColumns = [],
}: FetchDistinctValuesForColumnParams): Promise<ColumnType[]> {
  const response = await getTable(tableDefinitionId, {
    limit,
    useDistinct: true,
    columns: virtualColumnKey ? extraColumnsToInclude : [columnName, ...extraColumnsToInclude],
    virtualColumns: virtualColumnKey ? { [virtualColumnKey]: [columnName] } : undefined,
    filter,
    groupBy,
    distinctOnColumns,
  });

  return response.data.map((item) => item[columnName]);
}

export const MAX_CSV_EXPORT_ROWS = 10_000;

/**
 * Exports table data as a CSV file, respecting the provided filters and sorts, up to
 * MAX_CSV_EXPORT_ROWS rows.
 *
 * @returns `true` if the export was truncated at the row limit, `false` otherwise.
 */
export async function exportTableToCsv(
  tableId: string,
  params: {
    filter?: Filter;
    sorts?: { columnName: string; ascending: boolean }[];
    columnsToInclude?: string[];
    virtualColumns?: { vantage: string[] };
    conditionMonitorId?: string;
    columnHeaders?: Record<string, string>;
    columnOrder?: string[];
  },
  fallbackFilename?: string,
): Promise<boolean> {
  const response = await getAxiosInstance().post(
    `${APPSERVER_API_PREFIX}/tables/${tableId}/data/csv`,
    {
      limit: MAX_CSV_EXPORT_ROWS,
      filter: params.filter as unknown as SdkFilter,
      sorts: params.sorts,
      columnsToInclude: params.columnsToInclude,
      virtualColumns: params.virtualColumns,
      conditionMonitorId: params.conditionMonitorId,
      columnHeaders: params.columnHeaders,
      columnOrder: params.columnOrder,
    },
    { responseType: 'blob', headers: { Accept: 'text/csv' } },
  );

  downloadBlob({ blob: response.data as Blob, fallbackFilename });
  return response.headers['x-sq-export-truncated'] === 'true';
}
